import { isNil } from 'lodash';

import type { AiAction } from '~/components/AiChat/types';

/**
 * Task types whose cards were retired. Their rows still arrive — historically, and because the
 * AI service still emits several of them — so they are filtered out here. Leaving them merely
 * unmapped would display them by default and then fail the ACTION_TYPE_DESCRIPTOR lookup,
 * rendering an empty card and reporting an error.
 *
 * What is left renders a card: the invoice-analysis recommendation, the dynamic claim action,
 * and the demo types.
 */
export const RETIRED_ACTION_TASK_TYPES: ReadonlySet<string> = new Set([
  'decide_on_coverage',
  'update_reserve',
  'payment',
  'layer_advice',
  'http_general',
  'close_claim',
]);

const sortActionsByCreationDate = (a: AiAction, b: AiAction) => ((a.created_at || '') > (b.created_at || '') ? -1 : 1);

/**
 * Returns the actions that should be displayed, sorted by creation date (newest first).
 *
 * An action is displayable when it is not ignored, has a task type that has not been retired,
 * and its task type's display feature flag is enabled. Task types without a feature flag mapping
 * entry (e.g. http_general) are displayed by default.
 */
export const getDisplayableActions = (
  actions: AiAction[] | undefined,
  actionFlagEnabledMapping: Record<string, boolean>
): AiAction[] => {
  if (!actions) return [];

  return actions
    .filter((action: AiAction) => {
      const taskType = action.task_data?.task_type;

      return (
        !action.is_ignored &&
        taskType &&
        !RETIRED_ACTION_TASK_TYPES.has(taskType) &&
        (isNil(actionFlagEnabledMapping[taskType]) || actionFlagEnabledMapping[taskType])
      );
    })
    .sort(sortActionsByCreationDate);
};
